import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import styles from './ContentDetails.module.css';
import { SitePage } from '../../Model/siteMap';
import SiteTag from './SiteTag';

const BLOCK_DURATION = 0.2;
const WIPE_STAGGER_S = 0.12;

export const CONTENT_DETAIL_TRANSITION_ID = 'content-detail';

function isRoughlyOnScreen(el: HTMLElement | null): boolean {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
}

function isHttpUrl(href: string): boolean {
    return /^https?:\/\//i.test(href);
}

interface DetailWipeSectionProps {
    staggerInMs: number;
    staggerOutMs: number;
    phase: 'in' | 'out';
    transitionToken: number;
    onHideFinished: (token: number) => void;
    children: React.ReactNode;
}

const DetailWipeSection: React.FC<DetailWipeSectionProps> = ({
    staggerInMs,
    staggerOutMs,
    phase,
    transitionToken,
    onHideFinished,
    children,
}) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const blockerControls = useAnimationControls();
    const [contentOpacity, setContentOpacity] = useState(0);
    const cancelPaintRef = useRef(false);
    const completedHideTokenRef = useRef<number | null>(null);

    const runRevealPaint = useCallback(
        async (shouldWipe: boolean) => {
            cancelPaintRef.current = false;
            await new Promise<void>((resolve) => setTimeout(resolve, staggerInMs));
            if (cancelPaintRef.current) return;

            if (!shouldWipe) {
                blockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
                setContentOpacity(1);
                return;
            }

            await blockerControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (cancelPaintRef.current) return;
            setContentOpacity(1);
            await blockerControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
        },
        [blockerControls, staggerInMs],
    );

    const runHidePaint = useCallback(
        async (shouldWipe: boolean, token: number) => {
            cancelPaintRef.current = false;
            await new Promise<void>((resolve) => setTimeout(resolve, staggerOutMs));
            if (cancelPaintRef.current) return;

            if (!shouldWipe) {
                setContentOpacity(0);
                onHideFinished(token);
                return;
            }

            blockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
            await blockerControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (cancelPaintRef.current) return;
            setContentOpacity(0);
            await blockerControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (!cancelPaintRef.current) {
                onHideFinished(token);
            }
        },
        [blockerControls, onHideFinished, staggerOutMs],
    );

    useEffect(() => {
        if (phase !== 'in') return;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(wrapRef.current);
        void runRevealPaint(shouldWipe);

        return () => {
            cancelPaintRef.current = true;
            blockerControls.stop();
        };
    }, [blockerControls, phase, runRevealPaint]);

    useEffect(() => {
        if (phase !== 'out') return;

        if (completedHideTokenRef.current === transitionToken) return;
        completedHideTokenRef.current = transitionToken;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(wrapRef.current);
        void runHidePaint(shouldWipe, transitionToken);

        return () => {
            cancelPaintRef.current = true;
            blockerControls.stop();
        };
    }, [blockerControls, phase, runHidePaint, transitionToken]);

    return (
        <div ref={wrapRef} className={styles.wipeWrap}>
            <motion.div
                className={styles.wipeContent}
                initial={{ opacity: 0 }}
                animate={{ opacity: contentOpacity }}
                transition={{ duration: 0 }}
            >
                {children}
            </motion.div>
            <motion.div
                className={styles.wipeBlocker}
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={blockerControls}
                style={{ transformOrigin: 'left center' }}
            />
        </div>
    );
};

interface ContentDetailsProps {
    page: SitePage;
    phase: 'in' | 'out';
    transitionToken: number;
    onHideComplete: (id: string, token: number) => void;
}

const ContentDetails: React.FC<ContentDetailsProps> = ({
    page,
    phase,
    transitionToken,
    onHideComplete,
}) => {
    const hiddenCountRef = useRef(0);
    const preparedOutTokenRef = useRef<number | null>(null);

    const sections = useMemo(() => {
        const [firstImage, ...restImages] = page.images;
        const items: { key: string; node: React.ReactNode }[] = [];

        items.push({
            key: 'title',
            node: (
                <header className={styles.titleBlock}>
                    <h1 className={styles.title}>{page.title}</h1>
                    {page.tags.length > 0 ? (
                        <div className={styles.tagsRow} aria-label="Project tags">
                            {page.tags.map((tag, i) => (
                                <SiteTag key={`${page.id}-tag-${i}`} label={tag} variant="default" />
                            ))}
                        </div>
                    ) : null}
                </header>
            ),
        });

        if (firstImage) {
            const src = `/images/${page.id}/${firstImage}`;
            items.push({
                key: 'img-hero',
                node: (
                    <div className={styles.media}>
                        <img className={styles.heroImage} src={src} alt="" />
                    </div>
                ),
            });
        }

        items.push({
            key: 'description',
            node: (
                <div className={styles.descriptionBlock}>
                    {page.description.map((paragraph, i) => (
                        <p key={`${page.id}-desc-${i}`} className={styles.description}>
                            {paragraph}
                        </p>
                    ))}
                </div>
            ),
        });

        if (page.demoLink) {
            const href = page.demoLink;
            items.push({
                key: 'demo-link',
                node: (
                    <a
                        className={styles.demoButton}
                        href={href}
                        target={isHttpUrl(href) ? '_blank' : undefined}
                        rel={isHttpUrl(href) ? 'noopener noreferrer' : undefined}
                    >
                        {page.demoLinkTitle ?? 'Open'}
                    </a>
                ),
            });
        }

        for (const name of restImages) {
            items.push({
                key: `img-${name}`,
                node: (
                    <div className={styles.media}>
                        <img
                            className={styles.galleryImage}
                            src={`/images/${page.id}/${name}`}
                            alt=""
                        />
                    </div>
                ),
            });
        }

        return items;
    }, [page]);

    const blockTotal = sections.length;
    const staggerMs = WIPE_STAGGER_S * 1000;

    if (phase === 'out' && preparedOutTokenRef.current !== transitionToken) {
        preparedOutTokenRef.current = transitionToken;
        hiddenCountRef.current = 0;
    }
    if (phase === 'in') {
        preparedOutTokenRef.current = null;
    }

    const handleSectionHideFinished = useCallback(
        (token: number) => {
            if (token !== transitionToken) return;
            hiddenCountRef.current += 1;
            if (hiddenCountRef.current >= blockTotal) {
                onHideComplete(CONTENT_DETAIL_TRANSITION_ID, token);
            }
        },
        [blockTotal, onHideComplete, transitionToken],
    );

    return (
        <div className={styles.container}>
            <div className={styles.inner}>
                {sections.map((section, index) => {
                    const staggerInMs = index * staggerMs;
                    const staggerOutMs = 0;
                    return (
                        <DetailWipeSection
                            key={section.key}
                            staggerInMs={staggerInMs}
                            staggerOutMs={staggerOutMs}
                            phase={phase}
                            transitionToken={transitionToken}
                            onHideFinished={handleSectionHideFinished}
                        >
                            {section.node}
                        </DetailWipeSection>
                    );
                })}
            </div>
        </div>
    );
};

export default ContentDetails;
